import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";

// AGB / Términos y Condiciones — bilingual page (ES first, then DE) per legal
// requirement to display both versions verbatim. Style matches Impressum /
// Datenschutz: serif title, light-weight body, gold hairline dividers.

const ES_SECTIONS = [
    {
        n: "1",
        heading: "Naturaleza del servicio",
        body: "La Srta. Mercedes no ejerce como médica ni realiza diagnósticos, tratamientos médicos o prescripciones. Los servicios ofrecidos en esta plataforma consisten exclusivamente en coaching, asesoría en bienestar y acompañamiento personal. No constituyen terapia ni sustituyen atención médica profesional. En caso de que el Cliente necesite diagnóstico o tratamiento médico/psicológico, se le recomienda acudir a un profesional cualificado.",
    },
    {
        n: "2",
        heading: "Requisitos de participación y exención de responsabilidad",
        body: "Antes de iniciar cualquier programa de coaching o plan de entrenamiento, el Cliente debe asegurarse de encontrarse en condiciones físicas y psíquicas adecuadas. Se aconseja realizar un reconocimiento médico y obtener la aprobación de su médico de cabecera para participar. El Cliente asume toda responsabilidad por su salud y los resultados obtenidos; la Coach no garantiza un éxito específico y queda exenta de responsabilidad por cualquier daño derivado de la participación.",
    },
    {
        n: "3",
        heading: "Consulta inicial gratuita",
        body: "La única prestación gratuita que ofrece la Coach es la consulta inicial de 20 minutos (“Kennenlerngespräch”). Esta sesión sirve para que el Cliente conozca el enfoque de trabajo y plantee sus objetivos. No implica un diagnóstico médico ni la aceptación automática en un programa de coaching.",
    },
    {
        n: "4",
        heading: "Reservas, pagos y política de cancelación",
        body: "Las sesiones posteriores a la consulta gratuita son de pago. La reserva de citas se considera vinculante una vez efectuado el pago. No se realizan devoluciones ni reembolsos por cancelaciones tardías o por no presentarse a la cita, salvo que la cancelación se notifique con la antelación que determine la política de la Coach. En otros servicios, por ejemplo, se establece que las cancelaciones realizadas con menos de 48 horas de antelación o las ausencias injustificadas generan el cobro del 100 % de la tarifa.",
    },
    {
        n: "5",
        heading: "Vigencia y modificaciones",
        body: "Estas condiciones podrán actualizarse ocasionalmente. El Cliente acepta la versión vigente en el momento de su reserva. Para cualquier duda sobre estas cláusulas, puede dirigirse a la Coach a través de los datos de contacto proporcionados en la web.",
    },
];

const DE_SECTIONS = [
    {
        n: "1",
        heading: "Natur des Dienstes",
        body: "Frau Mercedes übt nicht als Ärztin aus und führt keine Diagnosen, medizinische Behandlungen oder Verordnungen durch. Die auf dieser Plattform angebotenen Leistungen bestehen ausschliesslich aus Coaching, Wellness-Beratung und persönlicher Begleitung. Sie stellen keine Therapie dar und ersetzen keine professionelle medizinische Betreuung. Wenn der Kunde eine medizinische oder psychologische Diagnose oder Behandlung benötigt, wird empfohlen, einen qualifizierten Fachmann zu konsultieren.",
    },
    {
        n: "2",
        heading: "Teilnahmevoraussetzungen und Haftungsausschluss",
        body: "Bevor Sie mit einem Coaching-Programm oder Trainingsplan beginnen, müssen Sie sicherstellen, dass Sie körperlich und psychisch geeignet sind. Es wird empfohlen, eine ärztliche Untersuchung durchzuführen und die Freigabe Ihres Hausarztes einzuholen. Der Kunde übernimmt die volle Verantwortung für seine Gesundheit und die erzielten Ergebnisse; der Coach garantiert keinen spezifischen Erfolg und ist von jeglicher Haftung befreit.",
    },
    {
        n: "3",
        heading: "Kostenlose Erstberatung",
        body: "Die einzige kostenlose Leistung des Coaches ist das 20-minütige Erstgespräch (“Kennenlerngespräch”). Diese Sitzung dient dazu, dass der Kunde den Arbeitsansatz kennenlernt und seine Ziele darlegt. Sie beinhaltet keine medizinische Diagnose und keine automatische Annahme in ein Coaching-Programm.",
    },
    {
        n: "4",
        heading: "Reservierung, Zahlung und Stornierungsbedingungen",
        body: "Die Sitzungen nach dem kostenlosen Erstgespräch sind kostenpflichtig. Die Reservierung eines Termins gilt als verbindlich, sobald die Zahlung erfolgt ist. Bei kurzfristigen Stornierungen oder Nichterscheinen erfolgt keine Rückerstattung, es sei denn, die Stornierung wird rechtzeitig mit der vom Coach definierten Vorlaufzeit mitgeteilt. In vergleichbaren Dienstleistungen wird z. B. festgelegt, dass Stornierungen weniger als 48 Stunden vor dem Termin oder Nichterscheinen zur vollständigen Berechnung der Gebühr führen.",
    },
    {
        n: "5",
        heading: "Geltung und Änderungen",
        body: "Diese Bedingungen können von Zeit zu Zeit aktualisiert werden. Der Kunde akzeptiert die zum Zeitpunkt seiner Buchung gültige Version. Bei Fragen zu diesen Bestimmungen kann der Kunde den Coach über die auf der Website angegebenen Kontaktdaten kontaktieren.",
    },
];

const Block = ({ titleLabel, languageCode, sections, testidPrefix }) => (
    <section
        data-testid={`agb-block-${languageCode}`}
        className="space-y-10 lg:space-y-12"
    >
        <header className="flex items-center gap-4">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-ella-ink text-ella-gold flex-shrink-0">
                <ScrollText className="w-5 h-5" strokeWidth={1.4} />
            </span>
            <div>
                <p className="ella-overline">{languageCode}</p>
                <h2 className="font-display text-3xl md:text-4xl text-ella-ink leading-tight mt-1">
                    {titleLabel}
                </h2>
            </div>
        </header>

        <ol className="space-y-9">
            {sections.map((s) => (
                <li
                    key={s.n}
                    data-testid={`${testidPrefix}-section-${s.n}`}
                    className="relative pl-12"
                >
                    <span className="absolute left-0 top-0 font-display text-ella-gold-dark text-sm tracking-[0.3em]">
                        0{s.n}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl text-ella-ink leading-tight">
                        {s.heading}
                    </h3>
                    <p className="mt-3 text-ella-ink/80 leading-relaxed font-light text-[15.5px]">
                        {s.body}
                    </p>
                </li>
            ))}
        </ol>
    </section>
);

const AGB = () => {
    const { t } = useLanguage();

    useEffect(() => {
        window.scrollTo({ top: 0 });
        const seo = t.page_meta?.seo?.agb;
        if (seo) document.title = seo.title;
    }, [t]);

    return (
        <div data-testid="agb-page" className="min-h-screen bg-ella-cream">
            <Header />
            <main className="pt-32 md:pt-40 pb-24">
                <div className="ella-container max-w-3xl">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ella-muted hover:text-ella-ink transition-colors"
                        data-testid="agb-back-home"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {t.legal.back}
                    </Link>

                    <span className="ella-overline mt-12 block" data-testid="agb-overline">
                        ELLA Online Coaching
                    </span>
                    <h1
                        className="ella-headline text-4xl md:text-5xl lg:text-6xl mt-3 text-ella-ink leading-[1.05]"
                        data-testid="agb-title"
                    >
                        Términos y Condiciones · AGB
                    </h1>
                    <p className="mt-5 text-ella-ink/65 italic font-light leading-relaxed">
                        Documento legal disponible en español y alemán. ·{" "}
                        Rechtsdokument auf Spanisch und Deutsch verfügbar.
                    </p>

                    <div className="mt-10 h-px w-24 bg-ella-gold" />

                    {/* Spanish first */}
                    <div className="mt-14">
                        <Block
                            languageCode="ES"
                            titleLabel="Términos y Condiciones"
                            sections={ES_SECTIONS}
                            testidPrefix="agb-es"
                        />
                    </div>

                    {/* Hairline divider between languages */}
                    <div className="my-20 flex items-center gap-4">
                        <div className="flex-1 h-px bg-ella-line" />
                        <span className="ella-overline text-ella-gold-dark">
                            Deutsche Fassung
                        </span>
                        <div className="flex-1 h-px bg-ella-line" />
                    </div>

                    {/* German */}
                    <Block
                        languageCode="DE"
                        titleLabel="Allgemeine Geschäftsbedingungen"
                        sections={DE_SECTIONS}
                        testidPrefix="agb-de"
                    />

                    <p
                        className="mt-20 pt-10 border-t border-ella-line italic text-ella-muted text-sm leading-relaxed"
                        data-testid="agb-footer-note"
                    >
                        © ELLA Online Coaching · Mercedes Köstel ·{" "}
                        {new Date().getFullYear()} — Estos términos se rigen por
                        la legislación suiza. / Diese Bedingungen unterliegen
                        dem schweizerischen Recht.
                    </p>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default AGB;
